import { fetcher } from '@/config/api';

// ─── Availability check ────────────────────────────────────────────────────

/**
 * Returns true when the browser supports WebAuthn platform authenticators
 * (Face ID, Touch ID, Windows Hello, fingerprint).
 */
export async function checkBiometricAvailability(): Promise<boolean> {
  if (!window.PublicKeyCredential) return false;
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

// ─── Base64url helpers (no npm deps — WebAuthn is native) ─────────────────

function bufferToBase64url(buffer: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

function base64urlToBuffer(base64url: string): ArrayBuffer {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const buffer = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    buffer[i] = binary.charCodeAt(i);
  }
  return buffer.buffer;
}

// ─── Registration ──────────────────────────────────────────────────────────

/**
 * Fetch PublicKeyCredentialCreationOptions from the backend and convert
 * binary fields from base64url to ArrayBuffer (required by the browser API).
 */
export async function getRegistrationOptions(): Promise<PublicKeyCredentialCreationOptions> {
  const raw = await fetcher<any>('/api/webauthn/register/options');
  return {
    ...raw,
    challenge: base64urlToBuffer(raw.challenge),
    user: {
      ...raw.user,
      id: base64urlToBuffer(raw.user.id),
    },
    excludeCredentials: (raw.excludeCredentials ?? []).map((c: any) => ({
      ...c,
      id: base64urlToBuffer(c.id),
    })),
  };
}

/**
 * Submit a newly created credential to the backend for verification and storage.
 */
export async function submitRegistration(
  credential: PublicKeyCredential,
  deviceName: string,
): Promise<{ success: boolean }> {
  const response = credential.response as AuthenticatorAttestationResponse;
  return fetcher<{ success: boolean }>('/api/webauthn/register', {
    method: 'POST',
    body: JSON.stringify({
      id: credential.id,
      rawId: bufferToBase64url(credential.rawId),
      type: credential.type,
      response: {
        clientDataJSON: bufferToBase64url(response.clientDataJSON),
        attestationObject: bufferToBase64url(response.attestationObject),
      },
      deviceName,
    }),
  });
}

// ─── Authentication ────────────────────────────────────────────────────────

/**
 * Fetch PublicKeyCredentialRequestOptions from the backend.
 * Returns null if the user has no registered credentials (404).
 */
export async function getAuthOptions(): Promise<PublicKeyCredentialRequestOptions | null> {
  try {
    const raw = await fetcher<any>('/api/webauthn/auth/options');
    return {
      ...raw,
      challenge: base64urlToBuffer(raw.challenge),
      allowCredentials: (raw.allowCredentials ?? []).map((c: any) => ({
        ...c,
        id: base64urlToBuffer(c.id),
      })),
    };
  } catch (err: any) {
    // 404 means the user has no registered credentials — not an error, just a state
    if (err?.message?.includes('404') || err?.status === 404) return null;
    throw err;
  }
}

/**
 * Submit the authenticator assertion to the backend for verification.
 */
export async function submitAuthentication(
  assertion: PublicKeyCredential,
): Promise<{ verified: boolean }> {
  const response = assertion.response as AuthenticatorAssertionResponse;
  return fetcher<{ verified: boolean }>('/api/webauthn/auth', {
    method: 'POST',
    body: JSON.stringify({
      id: assertion.id,
      rawId: bufferToBase64url(assertion.rawId),
      type: assertion.type,
      response: {
        clientDataJSON: bufferToBase64url(response.clientDataJSON),
        authenticatorData: bufferToBase64url(response.authenticatorData),
        signature: bufferToBase64url(response.signature),
        userHandle: response.userHandle ? bufferToBase64url(response.userHandle) : null,
      },
    }),
  });
}

// ─── Credential management ─────────────────────────────────────────────────

export interface WebAuthnCredential {
  id: string;
  deviceName: string;
  registeredAt: string;
  lastUsedAt: string;
  aaguid?: string;
}

export async function getCredentials(): Promise<WebAuthnCredential[]> {
  return fetcher<WebAuthnCredential[]>('/api/webauthn/credentials');
}

export async function deleteCredential(credentialId: string): Promise<void> {
  return fetcher<void>(
    `/api/webauthn/credentials/${encodeURIComponent(credentialId)}`,
    { method: 'DELETE' },
  );
}
